import { useMemo } from "react";
import { moduleNavItems } from "@/modules";
import { usePermissions } from "./usePermissions";
import type { INavItem } from "@/models/INavItem";

/**
 * Filter nav items based on user permissions.
 * - For items with children: show if ANY child is visible
 * - For leaf items: show if user has any of the required permissions
 *
 * @returns filtered nav items that the user has permission to see
 */
export function useFilteredNavItems(): {
  filteredNavItems: INavItem[];
  isLoading: boolean;
  hasNoAccess: boolean;
} {
  const { hasAnyPermission, isLoading, hasNoPermissions } = usePermissions();

  const filteredNavItems = useMemo(() => {
    // Show nothing until the permissions are known. Rendering every item optimistically
    // would flash modules the user cannot open, and briefly leak which ones exist.
    if (isLoading) {
      return [];
    }

    // With no permissions at all, only items that require none survive
    if (hasNoPermissions) {
      return moduleNavItems.filter((item) => {
        const hasNoPermissionRequirements =
          !item.permissions?.length &&
          !item.children?.some((child) => child.permissions?.length);
        return hasNoPermissionRequirements;
      });
    }

    return moduleNavItems
      .map((item) => {
        // If item has children, filter them first
        if (item.children && item.children.length > 0) {
          const filteredChildren = item.children.filter((child) => {
            // Show child if no permissions required or user has any required permission
            if (!child.permissions || child.permissions.length === 0) {
              return true;
            }
            return hasAnyPermission(child.permissions);
          });

          // Show parent if ANY child is visible
          if (filteredChildren.length === 0) {
            return null;
          }

          return { ...item, children: filteredChildren };
        }

        // Leaf item (no children)
        if (!item.permissions || item.permissions.length === 0) {
          return item;
        }

        return hasAnyPermission(item.permissions) ? item : null;
      })
      .filter((item): item is INavItem => item !== null);
  }, [hasAnyPermission, isLoading, hasNoPermissions]);

  // User has no access if not loading and no nav items are visible
  const hasNoAccess = !isLoading && filteredNavItems.length === 0;

  return {
    filteredNavItems,
    isLoading,
    hasNoAccess,
  };
}
